import type { analyticsAr } from './analytics.ar';

/** Performance, GPS anomalies and sync issues copy (English). Must cover exactly the same keys as `analyticsAr`. */
export const analyticsEn: typeof analyticsAr = {
  dashboard: {
    title: 'Performance',
    description: 'Visits submitted by field agents and their Zoho Desk sync outcome.',
    visits: 'Visits',
    synced: 'Synced to Zoho',
    partiallySynced: 'Partially synced',
    failed: 'Failed',
    inProgress: 'In progress',
    anomalies: 'Anomalies',
    avgDuration: 'Average duration',
    syncRate: 'Sync success',
    anomalyRate: 'Anomaly rate',
    byAgent: 'By agent',
    byBranch: 'By branch',
    byDay: 'Visits per day',
    allAgents: 'All agents',
    statusFailed: 'visits failed to sync in this range',
    statusPartial: 'visits partially synced in this range',
    statusHealthy: 'Sync working – no failed visits in this range',
    totalHint: 'Selected range',
    anomalyFilterHint: 'Filters the agent and branch tables',
    anomalyFilterOn: 'Rows with anomalies only',
    issues: {
      title: 'Issues reported',
      hint: 'Counted per project visit',
      projectVisits: 'Project visits',
      withIssue: 'With an issue',
      rate: 'Issue rate',
      count: 'Times reported',
      byService: 'By service',
      byIssueType: 'By issue type',
      byIssue: 'By issue',
      byBranch: 'Issues by branch',
      byDay: 'Issues per day',
    },
  },
  anomalies: {
    emptyFiltered: 'No anomalies match these filters.',
    emptyNone: 'No anomalies in this date range.',
    title: 'GPS anomalies',
    description: 'Visits whose location and timing checks reached the anomaly score threshold.',
    statusCount: 'anomalies in this range',
    statusCount_one: 'anomaly in this range',
    statusCount_two: 'anomalies in this range',
    statusCount_few: 'anomalies in this range',
    statusNone: 'No anomalies in this range',
    score: 'Score',
    minScore: 'Minimum score',
    minScorePlaceholder: 'Flagged from {{threshold}}',
    reasons: 'Reasons',
    distanceEnd: 'Check-out distance',
    ticket: 'Ticket',
    map: 'Map',
    table: 'Table',
    view: 'View',
    agentLocation: 'Agent location',
    branchLocation: 'Branch location',
    detailTitle: 'Visit anomaly',
    thresholds: 'Detection thresholds',
    maxAccuracy: 'Ignore GPS fixes less accurate than (m)',
    minDuration: 'Minimum visit duration (min)',
    maxDuration: 'Maximum visit duration (min)',
    maxClockSkew: 'Maximum device clock difference (min)',
    scoreThreshold: 'Flag as anomaly from score',
    weights: 'Weight per reason',
    superAdminOnly: 'Only a super admin can change them.',
    reason: {
      START_OUTSIDE_GEOFENCE: 'Check-in outside geofence',
      END_OUTSIDE_GEOFENCE: 'Check-out outside geofence',
      LOW_ACCURACY: 'Low GPS accuracy',
      MOCK_LOCATION: 'Mock location app detected',
      LOCATION_MISSING: 'Location missing',
      BRANCH_LOCATION_UNVERIFIED: 'Branch location not verified',
      UNLISTED_BRANCH: 'Branch not in catalog',
      DURATION_TOO_SHORT: 'Visit too short',
      DURATION_TOO_LONG: 'Visit too long',
      CLOCK_SKEW: 'Device clock differs from server',
      OVERLAPPING_VISIT: 'Overlaps another visit',
    },
  },
  sync: {
    openVisit: 'Open visit',
    emptyFiltered: 'No sync issues match this filter.',
    title: 'Zoho sync issues',
    description: 'Visits the sync worker could not fully deliver to Zoho Desk.',
    statusCount: 'visits need attention',
    statusCount_one: 'visit needs attention',
    statusCount_two: 'visits need attention',
    statusCount_few: 'visits need attention',
    statusNone: 'No sync issues',
    filter: 'Status',
    attempts: 'Attempts',
    lastError: 'Last error',
    retry: 'Retry sync',
    retried: 'Sync retried for visit {{visit}}',
    failedPhotos: 'Failed photos',
    pendingUploads: 'Photos not uploaded by device',
    photos: 'Photos',
    photosUploaded: '{{uploaded}} of {{total}} photos uploaded',
    detailTitle: 'Sync details',
    workerLog: 'Worker error',
    noError: 'No error recorded.',
  },
  fields: {
    visitId: 'Visit ID',
    submissionId: 'Submission ID',
    status: 'Status',
    zohoTicketNumber: 'Zoho ticket',
    syncAttempts: 'Sync attempts',
    receivedAt: 'Received',
    failedAttachments: 'Failed photos',
    pendingAttachmentUploads: 'Photos still on the phone',
    anomalyScore: 'Anomaly score',
    agent: 'Agent',
    brandName: 'Merchant',
    branchName: 'Branch',
    startedAt: 'Check-in',
    endedAt: 'Check-out',
    durationMinutes: 'Duration (min)',
    startLocation: 'Check-in location',
    endLocation: 'Check-out location',
    branchLocation: 'Branch location',
    distanceStartMeters: 'Distance at check-in (m)',
    distanceEndMeters: 'Distance at check-out (m)',
    reasonCode: 'Reason',
    weight: 'Weight',
    detail: 'Detail',
  },
};
